import { TelegramButton } from "./TelegramButton";
import React, { useState, useRef, useEffect, useCallback } from "react";
import { Sparkles, X, Send, Loader2, LifeBuoy, Bot, Headset, CheckCircle2, Inbox, Lock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const sid = (() => {
  let s = localStorage.getItem("ws_support_sid");
  if (!s) { s = Math.random().toString(36).slice(2) + Date.now().toString(36); localStorage.setItem("ws_support_sid", s); }
  return s;
})();

const SupportWidget = ({ user }) => {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("ai");
  const [msgs, setMsgs] = useState([
    { role: "assistant", text: "Hi 👋 I'm the WS AI Assistant. Ask me anything about connecting a broker, the AI Chart, Growth Zone, news alerts — or tap ‘Talk to the team’ for a human." },
  ]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({ name: user?.name || "", email: user?.email || "", message: "" });
  const [sent, setSent] = useState(false);
  const [tsending, setTsending] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [openCount, setOpenCount] = useState(0);
  const [active, setActive] = useState(null);
  const [reply, setReply] = useState("");
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, open, tab]);

  const loadTickets = useCallback(async () => {
    try {
      const r = await fetch(`${API}/support/tickets`, { credentials: "include" });
      if (r.ok) { const d = await r.json(); setIsAdmin(true); setTickets(d.tickets || []); setOpenCount(d.open_count || 0); }
    } catch { /* not admin */ }
  }, []);
  useEffect(() => { if (open) loadTickets(); }, [open, loadTickets]);

  const sendReply = async (id) => {
    if (!reply.trim()) return;
    const r = await fetch(`${API}/support/tickets/${id}/reply`, {
      method: "POST", credentials: "include",
      headers: { "Content-Type": "application/json" }, body: JSON.stringify({ reply }),
    });
    if (r.ok) { setReply(""); loadTickets(); }
  };
  const setStatus = async (id, status) => {
    const r = await fetch(`${API}/support/tickets/${id}/status`, {
      method: "POST", credentials: "include",
      headers: { "Content-Type": "application/json" }, body: JSON.stringify({ status }),
    });
    if (r.ok) loadTickets();
  };

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setInput("");
    setMsgs((m) => [...m, { role: "user", text }]);
    setSending(true);
    try {
      const r = await fetch(`${API}/support/chat`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sid, message: text }),
      });
      const d = await r.json();
      setMsgs((m) => [...m, { role: "assistant", text: d.reply || "Sorry, please try again." }]);
    } catch {
      setMsgs((m) => [...m, { role: "assistant", text: "Network hiccup — please try again or tap ‘Talk to the team’." }]);
    } finally { setSending(false); }
  };

  const submitTicket = async () => {
    if (!form.email.trim() || !form.message.trim() || tsending) return;
    setTsending(true);
    try {
      const r = await fetch(`${API}/support/ticket`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" }, body: JSON.stringify(form),
      });
      if (r.ok) setSent(true);
    } catch { /* noop */ } finally { setTsending(false); }
  };

  return (
    <>
      <button onClick={() => setOpen((o) => !o)} data-testid="support-fab" aria-label="Support"
        className="fixed bottom-5 right-5 z-[60] w-14 h-14 rounded-full flex items-center justify-center shadow-2xl transition-transform hover:scale-105 active:scale-95"
        style={{ background: "linear-gradient(135deg,#2563eb,#38bdf8)", boxShadow: "0 10px 30px rgba(56,189,248,0.5)" }}>
        <span className="absolute inset-0 rounded-full animate-ping opacity-30" style={{ background: "#38bdf8" }} />
        {open ? <X className="w-6 h-6 text-white relative" /> : <Sparkles className="w-6 h-6 text-white relative" />}
      </button>

      {open && (
        <div data-testid="support-panel"
          className="fixed z-[60] bg-[#0b1120] border border-[color:var(--ws-border-strong,rgba(148,163,184,0.25))] shadow-2xl flex flex-col overflow-hidden
                     inset-x-0 bottom-0 top-14 rounded-t-2xl
                     sm:inset-auto sm:bottom-24 sm:right-5 sm:top-auto sm:w-[380px] sm:h-[560px] sm:rounded-2xl">
          {/* Header */}
          <div className="px-4 py-3 flex items-center gap-2.5 border-b border-[color:var(--ws-border)]" style={{ background: "linear-gradient(135deg,rgba(37,99,235,0.25),rgba(56,189,248,0.12))" }}>
            <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: "linear-gradient(135deg,#2563eb,#38bdf8)" }}><LifeBuoy className="w-5 h-5 text-white" /></div>
            <div className="flex-1">
              <div className="ws-heading text-[14px] font-bold text-white">Wealth Secret Support</div>
              <div className="text-[11px] text-[color:var(--ws-muted)]">AI answers instantly · humans on request</div>
            </div>
            <button onClick={() => setOpen(false)} data-testid="support-close" className="w-8 h-8 rounded-lg flex items-center justify-center text-[color:var(--ws-muted)] hover:text-white hover:bg-[rgba(255,255,255,0.08)]"><X className="w-4 h-4" /></button>
          </div>

          <div className="px-3 pt-2.5 pb-1 flex items-center justify-between gap-2" data-testid="support-telegram-row">
            <span className="text-[11px] text-[color:var(--ws-muted)]">Prefer chat? Message the team directly:</span>
            <TelegramButton label="Telegram" testid="support-tg-btn" />
          </div>
          {/* Tabs */}
          <div className="flex gap-1 p-1.5 border-b border-[color:var(--ws-border)]">
            <button onClick={() => setTab("ai")} data-testid="support-tab-ai" className={`flex-1 py-2 rounded-lg text-[11px] font-semibold inline-flex items-center justify-center gap-1.5 ${tab === "ai" ? "ws-btn-primary" : "text-[color:var(--ws-muted)]"}`}><Bot className="w-4 h-4" /> AI</button>
            <button onClick={() => setTab("team")} data-testid="support-tab-team" className={`flex-1 py-2 rounded-lg text-[11px] font-semibold inline-flex items-center justify-center gap-1.5 ${tab === "team" ? "ws-btn-primary" : "text-[color:var(--ws-muted)]"}`}><Headset className="w-4 h-4" /> Team</button>
            {isAdmin && (
              <button onClick={() => { setTab("inbox"); loadTickets(); }} data-testid="support-tab-inbox" className={`flex-1 py-2 rounded-lg text-[11px] font-semibold inline-flex items-center justify-center gap-1.5 relative ${tab === "inbox" ? "ws-btn-primary" : "text-[color:var(--ws-muted)]"}`}>
                <Inbox className="w-4 h-4" /> Inbox
                {openCount > 0 && <span className="ml-0.5 text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-[#ef4444] text-white">{openCount}</span>}
              </button>
            )}
          </div>

          {tab === "ai" ? (
            <>
              <div className="flex-1 overflow-y-auto p-3 space-y-2.5" data-testid="support-messages">
                {msgs.map((m, i) => (
                  <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[82%] px-3 py-2 rounded-2xl text-[13px] leading-snug ${m.role === "user" ? "bg-[rgba(59,130,246,0.28)] text-white rounded-br-sm" : "bg-[rgba(255,255,255,0.06)] text-[color:var(--ws-text)] rounded-bl-sm"}`}>{m.text}</div>
                  </div>
                ))}
                {sending && <div className="flex justify-start"><div className="px-3 py-2 rounded-2xl bg-[rgba(255,255,255,0.06)]"><Loader2 className="w-4 h-4 animate-spin text-[color:var(--ws-blue-bright)]" /></div></div>}
                <div ref={endRef} />
              </div>
              <div className="p-2.5 border-t border-[color:var(--ws-border)] flex items-center gap-2">
                <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
                  data-testid="support-input" placeholder="Ask the WS AI…"
                  className="flex-1 bg-[rgba(10,17,33,0.8)] border border-[color:var(--ws-border)] rounded-xl text-[13px] text-white px-3 py-2.5 outline-none focus:border-[color:var(--ws-blue)]" />
                <button onClick={send} disabled={sending} data-testid="support-send" className="ws-btn-primary w-10 h-10 rounded-xl flex items-center justify-center shrink-0"><Send className="w-4 h-4" /></button>
              </div>
            </>
          ) : tab === "team" ? (
            <div className="flex-1 overflow-y-auto p-4">
              {sent ? (
                <div className="h-full flex flex-col items-center justify-center text-center gap-3" data-testid="support-ticket-success">
                  <CheckCircle2 className="w-12 h-12 text-[#34d399]" />
                  <div className="ws-heading text-white font-bold text-[15px]">Message sent!</div>
                  <div className="text-[13px] text-[color:var(--ws-muted)] max-w-[260px]">The Wealth Secret team has your request and will reach out to <span className="text-white">{form.email}</span> shortly.</div>
                  <button onClick={() => { setSent(false); setForm({ ...form, message: "" }); setTab("ai"); }} className="ws-btn-ghost px-3 py-2 rounded-lg text-[12px] font-semibold mt-1">Back to AI</button>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="text-[13px] text-[color:var(--ws-muted)]">Prefer a human? Leave a note and the team will get back to you.</div>
                  <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="support-name" placeholder="Your name"
                    className="w-full bg-[rgba(10,17,33,0.8)] border border-[color:var(--ws-border)] rounded-xl text-[13px] text-white px-3 py-2.5 outline-none focus:border-[color:var(--ws-blue)]" />
                  <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="support-email" placeholder="Email *" type="email"
                    className="w-full bg-[rgba(10,17,33,0.8)] border border-[color:var(--ws-border)] rounded-xl text-[13px] text-white px-3 py-2.5 outline-none focus:border-[color:var(--ws-blue)]" />
                  <textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="support-message" placeholder="How can we help? *" rows={5}
                    className="w-full bg-[rgba(10,17,33,0.8)] border border-[color:var(--ws-border)] rounded-xl text-[13px] text-white px-3 py-2.5 outline-none resize-none focus:border-[color:var(--ws-blue)]" />
                  <button onClick={submitTicket} disabled={tsending || !form.email.trim() || !form.message.trim()} data-testid="support-submit"
                    className="ws-btn-primary w-full py-2.5 rounded-xl text-[13px] font-bold inline-flex items-center justify-center gap-2 disabled:opacity-50">
                    {tsending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send to the team
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="flex-1 overflow-y-auto p-3 space-y-2" data-testid="support-inbox">
              {tickets.length === 0 && <div className="text-[13px] text-[color:var(--ws-muted)] py-4 text-center">No support tickets yet.</div>}
              {tickets.map((t) => (
                <div key={t.ticket_id} className="rounded-xl border border-[color:var(--ws-border)] overflow-hidden" data-testid="inbox-ticket">
                  <button onClick={() => setActive(active === t.ticket_id ? null : t.ticket_id)} className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-[rgba(255,255,255,0.04)]">
                    <span className="w-2 h-2 rounded-full shrink-0" style={{ background: t.status === "closed" ? "#22c55e" : t.status === "answered" ? "#38bdf8" : "#f59e0b" }} />
                    <div className="min-w-0 flex-1">
                      <div className="text-[12.5px] font-semibold text-white truncate">{t.name || "Guest"} · <span className="text-[color:var(--ws-muted)] font-normal">{t.email}</span></div>
                      <div className="text-[11px] text-[color:var(--ws-muted)] truncate">{t.message}</div>
                    </div>
                    <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded shrink-0" style={{ color: t.status === "closed" ? "#22c55e" : t.status === "answered" ? "#38bdf8" : "#f59e0b", background: "rgba(255,255,255,0.06)" }}>{t.status}</span>
                  </button>
                  {active === t.ticket_id && (
                    <div className="px-3 pb-3 pt-1 border-t border-[color:var(--ws-border)] space-y-2">
                      <div className="text-[12.5px] text-[color:var(--ws-text)] whitespace-pre-wrap">{t.message}</div>
                      {(t.replies || []).map((r, i) => (
                        <div key={i} className="text-[12px] text-white bg-[rgba(59,130,246,0.16)] rounded-lg px-2.5 py-1.5"><span className="text-[color:var(--ws-blue-bright)] font-semibold">{r.by}: </span>{r.text}</div>
                      ))}
                      <textarea value={active === t.ticket_id ? reply : ""} onChange={(e) => setReply(e.target.value)} data-testid="inbox-reply-input" placeholder="Write a reply…" rows={2}
                        className="w-full bg-[rgba(10,17,33,0.8)] border border-[color:var(--ws-border)] rounded-lg text-[12.5px] text-white px-2.5 py-2 outline-none resize-none focus:border-[color:var(--ws-blue)]" />
                      <div className="flex items-center gap-2">
                        <button onClick={() => sendReply(t.ticket_id)} disabled={!reply.trim()} data-testid="inbox-reply-send" className="ws-btn-primary px-3 py-1.5 rounded-lg text-[11px] font-semibold inline-flex items-center gap-1.5 disabled:opacity-50"><Send className="w-3.5 h-3.5" /> Reply</button>
                        {t.status !== "closed"
                          ? <button onClick={() => setStatus(t.ticket_id, "closed")} data-testid="inbox-close" className="ws-btn-ghost px-3 py-1.5 rounded-lg text-[11px] font-semibold">Close</button>
                          : <button onClick={() => setStatus(t.ticket_id, "open")} data-testid="inbox-reopen" className="ws-btn-ghost px-3 py-1.5 rounded-lg text-[11px] font-semibold">Reopen</button>}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default SupportWidget;
